import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';
import '@fontsource/gabarito/400.css';
import '@fontsource/gabarito/600.css';
import '@fontsource/gabarito/700.css';
import '@/App.css';

import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './lib/auth';
import { LanguageProvider } from './lib/i18n';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { CookieBanner } from './components/CookieBanner';

// Eagerly loaded — critical first-paint route (home)
import Landing from './pages/Landing';

// Lazy loaded — every other route is only fetched when navigated to.
// This dramatically reduces initial bundle size and Total Blocking Time (TBT).
const Vision = lazy(() => import('./pages/Vision'));
const Programs = lazy(() => import('./pages/Programs'));
const SummerSchool = lazy(() => import('./pages/SummerSchool'));
const CourseDetail = lazy(() => import('./pages/CourseDetail'));
const Certificates = lazy(() => import('./pages/Certificates'));
const Educators = lazy(() => import('./pages/Educators'));
const Contribute = lazy(() => import('./pages/Contribute'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const StudentDashboard = lazy(() => import('./pages/StudentDashboard'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Terms = lazy(() => import('./pages/Terms'));
const PerspectivesPage = lazy(() => import('./pages/PerspectivesPage'));
const ArticlePage = lazy(() => import('./pages/ArticlePage'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const VerifyCertificate = lazy(() => import('./pages/VerifyCertificate'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));

function RouteLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="route-loader">
      <div className="w-8 h-8 border-2 border-[#2D78F6] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="min-h-screen bg-[#E7EAEF]" data-testid="app-root">
            <Navbar />
            <Toaster position="top-right" richColors closeButton duration={4500} />
            <main className="min-h-screen">
              <Suspense fallback={<RouteLoader />}>
                <Routes>
                  <Route path="/" element={<Landing />} />
                  <Route path="/vision" element={<Vision />} />
                  <Route path="/programs" element={<Programs />} />
                  <Route path="/summer-school" element={<SummerSchool />} />
                  <Route path="/programs/:id" element={<CourseDetail />} />
                  <Route path="/certificates" element={<Certificates />} />
                  <Route path="/educators" element={<Educators />} />
                  <Route path="/contribute" element={<Contribute />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/dashboard" element={<StudentDashboard />} />
                  <Route path="/payment/success" element={<PaymentSuccess />} />
                  <Route path="/verify/:certNumber" element={<VerifyCertificate />} />
                  <Route path="/perspectives" element={<PerspectivesPage />} />
                  <Route path="/perspectives/:slug" element={<ArticlePage />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<Terms />} />
                </Routes>
              </Suspense>
            </main>
            <Footer />
            <CookieBanner />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
